import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../../components/Layout/Layout';
import { useUser } from '../../app/UserContext';
import { buildFeed } from '../../domain/leaderboard/feed';
import { computeMonthlyAwards } from '../../domain/awards/computeAwards';
import { getCurrentMonthKey } from '../../domain/workouts/types';
import { useAwardsData } from '../awards/useAwardsData';
import { FeedList } from './FeedList';
import { UserHistoryModal } from './UserHistoryModal';
import './LeaderboardPage.css';

const FEED_PAGE = 40;

/**
 * The activity feed: every lift by anyone, newest first, across all months.
 *
 * Its own route rather than a tab on the leaderboard - it shares no controls with
 * the board (no month, no filters) and the two were only ever grouped because
 * they happened to read the same collection.
 */
export function FeedPage() {
  const { user } = useUser();
  const navigate = useNavigate();
  const [visible, setVisible] = useState(FEED_PAGE);
  const [selectedUser, setSelectedUser] = useState<{ id: string; name: string } | null>(null);

  // The same read the trophy case and the leaderboard's award view use: the
  // feed needs every lift anyway, and the month recaps need the scoring sex the
  // plain name lookup does not carry.
  const { data, isLoading } = useAwardsData();

  const items = useMemo(() => {
    if (!data) return [];
    const userNames = Object.fromEntries(
      Object.entries(data.users).map(([id, u]) => [id, u.displayName]),
    );
    return buildFeed(data.workouts, userNames);
  }, [data]);

  /** Each finished month's results, keyed by month, to head that month's lifts. */
  const awardsByMonth = useMemo(() => {
    if (!data) return {};
    return Object.fromEntries(
      computeMonthlyAwards(data.workouts, data.users).map((m) => [m.monthKey, m.social]),
    );
  }, [data]);

  return (
    <Layout>
      <div className="leaderboard">
        <div className="leaderboard__header">
          <h1 className="leaderboard__title">Feed</h1>
        </div>

        <div className="leaderboard__content">
          {isLoading ? (
            <div className="leaderboard__loading">
              <div className="leaderboard__pulse">Loading...</div>
            </div>
          ) : (
            <FeedList
              items={items}
              currentUserId={user?.id}
              visibleCount={visible}
              onShowMore={() => setVisible((n) => n + FEED_PAGE)}
              onSelectUser={(id, name) => setSelectedUser({ id, name })}
              awardsByMonth={awardsByMonth}
              onSeeMonth={(monthKey) => navigate(`/leaderboard?awards=${monthKey}`)}
            />
          )}
        </div>
      </div>

      {selectedUser && (
        <UserHistoryModal
          userId={selectedUser.id}
          displayName={selectedUser.name}
          initialMonthKey={getCurrentMonthKey()}
          onClose={() => setSelectedUser(null)}
        />
      )}
    </Layout>
  );
}
